import { useState, useRef, useEffect, useCallback } from 'react'

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface PendingAction {
  tool_use_id: string;
  tool_name: string;
  input: Record<string, unknown>;
}

interface ChatResponse {
  message: string;
  needs_confirmation?: boolean;
  pending_action?: PendingAction;
  conversation_id?: number;
}

interface ConversationResponse {
  conversation: { id: number; title: string | null } | null;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
}

interface ChatAssistantProps {
  isOpen: boolean;
  onClose: () => void;
}

// Helper to extract bulk operation count from pending action
function getBulkInfo(action: PendingAction): { count: number; label: string } {
  const input = action.input
  switch (action.tool_name) {
    case 'delete_recurring': {
      const ids = (input.recurring_ids as number[]) || (input.recurring_id ? [input.recurring_id] : [])
      return { count: ids.length, label: 'recurring item' }
    }
    case 'delete_transaction': {
      const ids = (input.transaction_ids as number[]) || (input.transaction_id ? [input.transaction_id] : [])
      return { count: ids.length, label: 'transaction' }
    }
    case 'delete_asset': {
      const ids = (input.asset_ids as number[]) || (input.asset_id ? [input.asset_id] : [])
      return { count: ids.length, label: 'asset' }
    }
    case 'delete_debt': {
      const ids = (input.debt_ids as number[]) || (input.debt_id ? [input.debt_id] : [])
      return { count: ids.length, label: 'debt' }
    }
    case 'delete_goal': {
      const ids = (input.goal_ids as number[]) || (input.goal_id ? [input.goal_id] : [])
      return { count: ids.length, label: 'goal' }
    }
    default:
      return { count: 1, label: 'item' }
  }
}

// Helper to generate a human-readable description of pending action
function getActionDescription(action: PendingAction): string {
  const input = action.input
  const formatAmount = (amt: unknown) => typeof amt === 'number' ? `$${amt.toLocaleString()}` : ''
  const formatFreq = (freq: unknown) => {
    const freqMap: Record<string, string> = {
      'weekly': 'weekly',
      'bi-weekly': 'bi-weekly',
      'semi-monthly': 'twice monthly',
      'monthly': 'monthly',
      'quarterly': 'quarterly',
      'annual': 'annually'
    }
    return typeof freq === 'string' ? freqMap[freq] || freq : ''
  }

  switch (action.tool_name) {
    case 'add_recurring': {
      const desc = input.description as string || 'recurring item'
      const amount = formatAmount(input.amount)
      const freq = formatFreq(input.frequency) || 'monthly'
      const type = input.type as string || 'expense'
      return `Add "${desc}" as ${freq} ${type} ${amount}`
    }
    case 'add_transaction': {
      const desc = input.description as string || input.category as string || 'transaction'
      const amount = formatAmount(input.amount)
      const type = input.type as string || 'expense'
      return `Add ${type}: ${desc} ${amount}`
    }
    case 'add_goal': {
      const name = input.name as string || 'goal'
      const target = formatAmount(input.target_amount)
      return `Create goal "${name}" with target ${target}`
    }
    case 'add_asset': {
      const name = input.name as string || 'asset'
      const value = formatAmount(input.current_value)
      return `Add asset "${name}" worth ${value}`
    }
    case 'add_debt': {
      const name = input.name as string || 'debt'
      const principal = formatAmount(input.principal)
      return `Add debt "${name}" of ${principal}`
    }
    case 'delete_recurring': {
      const ids = (input.recurring_ids as number[]) || (input.recurring_id ? [input.recurring_id] : [])
      return ids.length > 1 ? `Delete ${ids.length} recurring items` : 'Delete recurring item'
    }
    case 'delete_transaction': {
      const ids = (input.transaction_ids as number[]) || (input.transaction_id ? [input.transaction_id] : [])
      return ids.length > 1 ? `Delete ${ids.length} transactions` : 'Delete transaction'
    }
    case 'delete_asset': {
      const ids = (input.asset_ids as number[]) || (input.asset_id ? [input.asset_id] : [])
      return ids.length > 1 ? `Delete ${ids.length} assets` : 'Delete asset'
    }
    case 'delete_debt': {
      const ids = (input.debt_ids as number[]) || (input.debt_id ? [input.debt_id] : [])
      return ids.length > 1 ? `Delete ${ids.length} debts` : 'Delete debt'
    }
    case 'delete_goal': {
      const ids = (input.goal_ids as number[]) || (input.goal_id ? [input.goal_id] : [])
      return ids.length > 1 ? `Delete ${ids.length} goals` : 'Delete goal'
    }
    case 'update_recurring':
      return `Update recurring item${input.description ? `: "${input.description}"` : ''}`
    case 'update_transaction':
      return 'Update transaction'
    case 'update_asset':
      return `Update asset${input.name ? `: "${input.name}"` : ''}`
    case 'update_debt':
      return `Update debt${input.name ? `: "${input.name}"` : ''}`
    case 'update_goal':
      return `Update goal${input.name ? `: "${input.name}"` : ''}`
    default:
      return `Confirm ${action.tool_name.replace(/_/g, ' ')}`
  }
}

const DEFAULT_GREETING = "Hi! I'm your budget assistant. I can help you understand your spending, track goals, and explore 'what if' scenarios. What would you like to know?";

export function ChatAssistant({ isOpen, onClose }: ChatAssistantProps) {
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: DEFAULT_GREETING },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [conversationId, setConversationId] = useState<number | undefined>();
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea as user types
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load conversation history when chat opens
  const loadConversationHistory = useCallback(async () => {
    if (hasLoadedHistory) return;

    setIsLoadingHistory(true);
    try {
      const response = await fetch("/api/conversations");
      if (!response.ok) {
        console.error("Failed to load conversation history");
        return;
      }

      const data = await response.json() as ConversationResponse;

      if (data.conversation && data.messages.length > 0) {
        // We have an existing conversation - load it
        setConversationId(data.conversation.id);
        setMessages(data.messages.map(m => ({
          role: m.role,
          content: m.content
        })));
      }
      // If no conversation, keep the default greeting
    } catch (error) {
      console.error("Error loading conversation history:", error);
    } finally {
      setIsLoadingHistory(false);
      setHasLoadedHistory(true);
    }
  }, [hasLoadedHistory]);

  // Load history when chat opens
  useEffect(() => {
    if (isOpen && !hasLoadedHistory) {
      loadConversationHistory();
    }
  }, [isOpen, hasLoadedHistory, loadConversationHistory]);

  // Start a new conversation
  const startNewConversation = async () => {
    try {
      // Mark existing conversation as inactive
      await fetch("/api/conversations", { method: "DELETE" });

      // Reset local state
      setConversationId(undefined);
      setMessages([{ role: "assistant", content: DEFAULT_GREETING }]);
      setPendingAction(null);
      setHasLoadedHistory(true); // Prevent reloading old conversation
    } catch (error) {
      console.error("Error starting new conversation:", error);
    }
  };

  const sendMessage = async (userMessage?: string, confirmAction?: {
    tool_use_id: string;
    tool_name: string;
    input: Record<string, unknown>;
    confirmed: boolean
  }) => {
    const messageToSend = userMessage || input;
    if (!messageToSend.trim() && !confirmAction) return;

    const newMessages: Message[] = confirmAction
      ? messages
      : [...messages, { role: "user" as const, content: messageToSend }];

    if (!confirmAction) {
      setMessages(newMessages);
      setInput("");
      // Reset textarea height after clearing
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }

    setIsLoading(true);

    try {
      // Use new API format: single message + conversation_id
      // Falls back to legacy format for confirm_action
      const requestBody = confirmAction
        ? {
            messages: newMessages,
            confirm_action: confirmAction,
          }
        : {
            message: messageToSend,
            conversation_id: conversationId,
          };

      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message");
      }

      const chatResponse = data as ChatResponse;

      // Track conversation ID for memory persistence
      if (chatResponse.conversation_id) {
        setConversationId(chatResponse.conversation_id);
      }

      if (chatResponse.needs_confirmation && chatResponse.pending_action) {
        setPendingAction(chatResponse.pending_action);
      } else {
        setPendingAction(null);
      }

      setMessages([
        ...newMessages,
        { role: "assistant", content: chatResponse.message },
      ]);
    } catch (error) {
      console.error("Chat error:", error);
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content: `Sorry, I encountered an error: ${errorMsg}`,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (confirmed: boolean) => {
    if (!pendingAction) return;

    if (confirmed) {
      setMessages([
        ...messages,
        { role: "user", content: "Yes, please proceed." },
      ]);
    } else {
      setMessages([
        ...messages,
        { role: "user", content: "No, cancel that." },
        { role: "assistant", content: "Okay, I won't make any changes." },
      ]);
      setPendingAction(null);
      return;
    }

    await sendMessage(undefined, {
      tool_use_id: pendingAction.tool_use_id,
      tool_name: pendingAction.tool_name,
      input: pendingAction.input,
      confirmed,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end p-4 sm:p-6">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Chat Window */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md h-[600px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone/10 bg-forest text-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🤖</span>
            <div>
              <h3 className="font-semibold">Budget Assistant</h3>
              <p className="text-xs text-white/70">Powered by Claude</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={startNewConversation}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors text-sm"
              title="Start new conversation"
            >
              ↺
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoadingHistory && (
            <div className="flex justify-center py-8">
              <div className="flex items-center gap-2 text-stone">
                <div className="w-2 h-2 bg-forest rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-forest rounded-full animate-bounce delay-100" />
                <div className="w-2 h-2 bg-forest rounded-full animate-bounce delay-200" />
                <span className="text-sm ml-2">Loading conversation...</span>
              </div>
            </div>
          )}
          {!isLoadingHistory && messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                  message.role === "user"
                    ? "bg-forest text-white rounded-br-sm"
                    : "bg-cream text-charcoal rounded-bl-sm"
                }`}
              >
                <p className="whitespace-pre-wrap text-sm">{message.content}</p>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-cream text-charcoal rounded-2xl rounded-bl-sm px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-forest rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-forest rounded-full animate-bounce delay-100" />
                  <div className="w-2 h-2 bg-forest rounded-full animate-bounce delay-200" />
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Buttons */}
          {pendingAction && !isLoading && (() => {
            const bulkInfo = getBulkInfo(pendingAction)
            const isBulk = bulkInfo.count > 1
            const actionDesc = getActionDescription(pendingAction)
            return (
              <div className="flex flex-col items-center gap-2 py-2">
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800">
                  <span className="font-medium">Confirm action:</span> {actionDesc}
                </div>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => handleConfirm(false)}
                    className="px-4 py-2 border border-stone/20 text-charcoal rounded-lg hover:bg-stone/5 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleConfirm(true)}
                    className="px-4 py-2 bg-forest text-white rounded-lg hover:bg-forest-dark transition-colors"
                  >
                    {isBulk ? `Confirm All (${bulkInfo.count})` : 'Confirm'}
                  </button>
                </div>
              </div>
            )
          })()}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-stone/10">
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                adjustTextareaHeight();
              }}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your budget..."
              className="flex-1 resize-none rounded-xl border border-stone/20 px-4 py-3 text-sm focus:ring-2 focus:ring-forest focus:border-transparent max-h-48 min-h-[44px] overflow-hidden"
              rows={1}
              disabled={isLoading}
            />
            <button
              onClick={() => sendMessage()}
              disabled={isLoading || !input.trim()}
              className="p-3 bg-forest text-white rounded-xl hover:bg-forest-dark transition-colors disabled:opacity-50"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            </button>
          </div>
          <p className="text-xs text-stone text-center mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
}
